import { _js } from '@ifixit/localize';
import { useRef, useEffect, useState, ReactNode, useImperativeHandle } from 'react';
import type { Ref } from 'react';
import { faExternalLinkAlt, faPencilAlt, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Input } from '@ifixit/ui/components/input';
import { Label } from '@ifixit/ui/components/label';
import { Switch } from '@ifixit/ui/components/switch';
import { cn } from '@ifixit/ui/lib/utils';
import { TiptapButton } from '../../components/TiptapButton';
import type { Editor, Range } from '@tiptap/core';
import { useEditorContext } from '../../editor_context';
import {
   activeLinkMark,
   activeLinkOpensNewWindow,
   isLinkMarkActive,
   wikiLinkSaveTarget,
   type LinkMarkName,
} from './link_marks';

declare type LinkEditorState = 'preview' | 'edit';
export type { LinkEditorState };

// Mirrors the former @ifixit/primitives shadow[3] elevation.
const linkEditorPanel =
   'rounded-md border border-gray-300 bg-gray-50 p-2.5 shadow-[0_2px_4px_rgba(17,22,26,0.1),0_8px_16px_rgba(17,22,26,0.2)]';

const LinkPreview = (props: any) => {
   if (props.editor.isDestroyed || !isLinkMarkActive(props.editor)) {
      return null;
   }

   return (
      <div
         // Hosts (the step line editor) use this to tell "focus moved into
         // the link bubble" apart from "focus left the editor".
         data-link-editor-bubble=""
         className={cn(
            linkEditorPanel,
            'flex max-w-[min(350px,90vw)] md:max-w-[min(500px,90vw)] lg:max-w-[min(600px,90vw)]'
         )}
      >
         <a
            href={props.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex min-w-0 grow-[2] items-center gap-2 text-brand-500"
         >
            <FontAwesomeIcon icon={faExternalLinkAlt} className="text-gray-500" />
            <span className="line-clamp-1 min-w-0 break-words text-base">{props.url}</span>
         </a>
         <TiptapButton
            icon={<FontAwesomeIcon icon={faPencilAlt} />}
            onClick={props.handleEdit}
            title={_js('Edit link')}
            className="ml-2 min-h-10 min-w-10"
         />
         <TiptapButton
            icon={<FontAwesomeIcon icon={faTrash} />}
            onClick={props.handleRemove}
            title={_js('Remove link')}
            className="ml-2 min-h-10 min-w-10"
         />
      </div>
   );
};

const LinkEdit = (props: any) => {
   const { editor, url, setUrl, text, setText, newTab, setNewTab, handleSave, handleCancel } =
      props;

   const textRef = useRef<HTMLInputElement>(null);
   const urlRef = useRef<HTMLInputElement>(null);

   useEffect(() => {
      const focusText = () => {
         textRef.current?.focus();
         setNewTab(activeLinkOpensNewWindow(editor));
      };
      const focusUrl = () => urlRef.current?.focus();
      // If we're editing an existing link, focus the link text and set the newTab switch to previous value
      const focusCallback = url ? focusText : focusUrl;
      setTimeout(focusCallback, 100);
   }, []);

   const handleKeyDown = (event: any) => {
      if (event.key !== 'Enter') {
         return;
      }
      event.preventDefault();
      if (url) {
         handleSave();
      }
   };

   const handleChangeUrl = (event: any) => {
      setUrl(event.target.value);
   };

   const handleChangeText = (event: any) => {
      setText(event.target.value);
   };

   return (
      <div
         data-link-editor-bubble=""
         className={cn(
            linkEditorPanel,
            'flex w-[350px] max-w-[90vw] flex-col gap-2 md:w-[500px] lg:w-[600px]'
         )}
      >
         <div className="flex flex-col gap-1">
            <Label htmlFor="link-text">{_js('Link text')}</Label>
            <Input
               id="link-text"
               className="text-sm"
               value={text}
               onChange={handleChangeText}
               onKeyDown={handleKeyDown}
               ref={textRef}
            />
         </div>
         <div className="flex flex-col gap-1">
            <Label htmlFor="link-address">{_js('Link address')}</Label>
            <Input
               id="link-address"
               className="text-sm"
               placeholder="https://www.example.com"
               value={url}
               onChange={handleChangeUrl}
               onKeyDown={handleKeyDown}
               ref={urlRef}
               type="url"
            />
         </div>
         <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="flex items-center gap-2">
               <Label htmlFor="newTabSwitch">{_js('Open in a new tab')}</Label>
               <Switch
                  id="newTabSwitch"
                  checked={newTab}
                  onCheckedChange={() => setNewTab(!newTab)}
               />
            </div>
            <div className="flex justify-end gap-2 sm:ml-auto">
               <button type="button" onClick={handleCancel} className="button-secondary button-md">
                  {_js('Cancel')}
               </button>
               <button
                  type="button"
                  onClick={handleSave}
                  disabled={!url}
                  className="button-primary button-md"
               >
                  {_js('Confirm Link')}
               </button>
            </div>
         </div>
      </div>
   );
};

export function LinkEditorBubble({
   ref,
   ...props
}: {
   children?: ReactNode;
   editor: Editor;
   ref?: Ref<unknown>;
}) {
   const { editor } = props;
   const { preset } = useEditorContext();
   const defaultLinkTarget = preset === 'guide_intro' ? '_blank' : '';
   const [url, setUrl] = useState('');
   const [text, setText] = useState('');
   const [newTab, setNewTab] = useState(defaultLinkTarget === '_blank');
   const [state, setState] = useState('preview');
   const [selection, setSelection] = useState<Range>({ from: 0, to: 0 });
   // Which mark the bubble is currently editing, its attrs, and its original
   // display text. Kept so a wikilink can be re-applied on save without losing
   // its page target/tag, and so a bare wikilink can be pinned to that page
   // when its text changes (see handleSave).
   const [linkMark, setLinkMark] = useState<LinkMarkName | null>(null);
   const [markAttrs, setMarkAttrs] = useState<Record<string, any>>({});
   const [markText, setMarkText] = useState('');

   const updateLinkState = () => {
      const { from, to } = editor.state.selection;
      setSelection({ from, to });

      const selectedText = editor.state.doc.textBetween(from, to);
      setText(selectedText);
      setMarkText(selectedText);

      const mark = activeLinkMark(editor);
      const attrs = mark ? editor.getAttributes(mark) : {};
      setLinkMark(mark);
      setMarkAttrs(attrs);
      setUrl(attrs.href || '');
      setNewTab(mark ? activeLinkOpensNewWindow(editor) : defaultLinkTarget === '_blank');
   };

   useImperativeHandle(ref, () => ({
      show: (state?: LinkEditorState) => {
         if (state) {
            setState(state);
         }
         updateLinkState();
         return true;
      },
      hide: () => {
         setText('');
         setUrl('');
         setState('preview');
         return true;
      },
   }));

   const handleSave = () => {
      // Editing a [[wikilink]] whose address wasn't changed: keep it a
      // wikilink (preserving its page target, tag, and rename/red-link
      // tracking) and only update the display text and new-window. Routing it
      // through the URL-link path below would drop those attrs and mangle its
      // root-relative href. Changing the address falls through and converts it
      // to a plain link, which is the "change it" the URL field offers.
      if (linkMark === 'wikiLink' && url === (markAttrs.href || '')) {
         const displayText = text || markText;
         const target = wikiLinkSaveTarget(markAttrs, markText, displayText);
         const wikiContent = {
            type: 'text',
            marks: [{ type: 'wikiLink', attrs: { ...markAttrs, target, newWindow: newTab } }],
            text: displayText,
         };
         editor.chain().focus().insertContentAt(selection, wikiContent).run();
         setState('preview');
         return;
      }

      let href = url;
      const urlHasProtocol = /^[a-z][\d+.a-z-]*:\/\//i.test(href);
      // Root-relative hrefs (e.g. a wiki page's /Wiki/E-Waste) are already
      // valid; only bare hosts like "example.com" need a protocol.
      const isRootRelative = href.startsWith('/');

      const target = newTab ? '_blank' : '';

      if (href.includes('mailto:')) {
         setUrl(href);
      } else if (href && !urlHasProtocol && !isRootRelative) {
         href = 'https://' + href;
         setUrl(href);
      }

      const replacementText = text || href;
      const linkContent = {
         type: 'text',
         marks: [{ type: 'link', attrs: { href, target } }],
         text: replacementText,
      };

      editor.chain().focus().insertContentAt(selection, linkContent).run();
      setState('preview');
   };

   const handleRemove = () => {
      const mark = activeLinkMark(editor);
      if (!mark) {
         return;
      }
      editor.chain().focus().extendMarkRange(mark).unsetMark(mark).run();
   };

   const handleCancel = () => {
      const mark = activeLinkMark(editor);
      const attrs = mark ? editor.getAttributes(mark) : {};
      const href = attrs.href || '';
      if (href) {
         setUrl(href);
      } else {
         handleRemove();
      }
      setNewTab(mark ? activeLinkOpensNewWindow(editor) : defaultLinkTarget === '_blank');
      editor.commands.focus();
      setState('preview');
   };

   const handleEdit = () => {
      const mark = activeLinkMark(editor);
      if (mark) {
         editor.commands.extendMarkRange(mark);
      }
      updateLinkState();
      setState('edit');
   };

   const innerProps = {
      editor,
      url,
      text,
      setUrl,
      setText,
      newTab,
      setNewTab,
      handleEdit,
      handleSave,
      handleRemove,
      handleCancel,
   };

   if (!state) {
      return null;
   }

   return (
      editor &&
      (state == 'preview' ? <LinkPreview {...innerProps} /> : <LinkEdit {...innerProps} />)
   );
}
